import {
  API_ROUTES,
  fetchUserFileDocument,
  postEncryptedJson,
} from "../../utils/apiClient";
import AppConstants from "../../utils/appConstants";
import { toApiDateParam } from "../../utils/dateRangeApiFormat";

/**
 * Transaction History Controller
 * Calls the encrypted get-all-transactions endpoint and normalizes the result.
 *
 * API: {{base_url}}/v1/user/get-all-transactions?page=0
 * BODY (logical structure before encryption):
 * {
 *   "PER_PAGE": 10,
 *   "TYPE": -1,
 *   "DATE_FROM": "2026-01-01T00:00:00.000" | null,
 *   "DATE_TO": "2026-02-02T00:00:00.000" | null,
 *   "SEARCH": null,
 *   "CURRENCY": null,
 *   "STATUS": null,
 *   "WALLET_ID": null
 * }
 */
class TransactionHistoryController {
  static _inFlight = new Map();
  static _cache = new Map();
  static _cacheTtlMs = 20 * 1000;

  /**
   * Fetch transactions from backend with encryption handled by apiClient.
   *
   * @param {Object} params
   * @param {number} [params.page=0] - Zero-based page index (also sent as ?page=).
   * @param {number} [params.perPage=10] - Items per page.
   * @param {number} [params.type=-1] - Transaction type filter (-1 = all, others backend-specific).
   * @param {string|null} [params.dateFrom=null] - ISO-like string "YYYY-MM-DDTHH:mm:ss.SSS" or null.
   * @param {string|null} [params.dateTo=null] - ISO-like string "YYYY-MM-DDTHH:mm:ss.SSS" or null.
   * @param {string|null} [params.search=null] - Free-text search.
   * @param {number|string|null} [params.currency=null] - Currency filter (backend-specific).
   * @param {number|string|null} [params.status=null] - Status filter (backend-specific).
   * @param {number|string|null} [params.walletId=null] - Wallet filter.
   * @param {Object} [options] - Optional postEncryptedJson options (e.g., baseURL override).
   *
   * @returns {Promise<{
   *   items: Array,
   *   pagination: { page: number, perPage: number, total?: number, lastPage?: number },
   *   raw: any
   * }>}
   */
  static async getAllTransactions(
    {
      page = 0,
      perPage = 10,
      type = -1,
      dateFrom = null,
      dateTo = null,
      search = null,
      currency = null,
      status = null,
      walletId = null,
    } = {},
    options = {}
  ) {
    const apiDateFrom = toApiDateParam(dateFrom, false);
    const apiDateTo = toApiDateParam(dateTo, true);

    const requestKey = JSON.stringify({
      page,
      perPage,
      type,
      dateFrom: apiDateFrom,
      dateTo: apiDateTo,
      search: search || null,
      currency: currency ?? null,
      status: status ?? null,
      walletId: walletId ?? null,
      baseURL: options?.baseURL || "",
    });

    const cached = TransactionHistoryController._cache.get(requestKey);
    if (
      cached &&
      typeof cached.timestamp === "number" &&
      Date.now() - cached.timestamp < TransactionHistoryController._cacheTtlMs
    ) {
      return cached.value;
    }

    const inFlight = TransactionHistoryController._inFlight.get(requestKey);
    if (inFlight) {
      return await inFlight;
    }

    const run = async () => {
    // Build BODY exactly as required (before encryption)
    const body = {
      PER_PAGE: perPage,
      TYPE: type,
      DATE_FROM: apiDateFrom,
      DATE_TO: apiDateTo,
      SEARCH: search || null,
      CURRENCY: currency ?? null,
      STATUS: status ?? null,
      WALLET_ID: walletId ?? null,
    };

    // Laravel uses 1-based pagination; UI uses 0-based page index
    const urlWithPage = `${API_ROUTES.GET_ALL_TRANSACTIONS}?page=${page + 1}`;

    const res = await postEncryptedJson(urlWithPage, body, options);
    const d = res.decryptedData || res.data || {};


    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    // Server returns CODE 200 and DATA as a Laravel paginator object:
    // {
    //   current_page: 1,
    //   data: [ ...transactions... ],
    //   per_page: 10,
    //   total: 32,
    //   last_page: 4,
    //   ...
    // }
    const dataBlock = d.DATA || {};
    let list = [];

    if (Array.isArray(dataBlock.data)) {
      list = dataBlock.data;
    } else if (Array.isArray(dataBlock.DATA)) {
      list = dataBlock.DATA;
    } else if (Array.isArray(dataBlock.items)) {
      list = dataBlock.items;
    } else if (Array.isArray(dataBlock.transactions)) {
      list = dataBlock.transactions;
    } else if (Array.isArray(dataBlock.list)) {
      list = dataBlock.list;
    }

    // Normalize a minimal view model for the UI but keep raw item too
    const items = list.map((item) => {
      const id =
        item.TRANSACTION_ID ??
        item.REF_ID ??
        item.REFID ??
        item.REFERENCE_ID ??
        item.ID ??
        item.id ??
        null;

      const date =
        item.TRANSACTION_DATE ||
        item.TRX_DATE ||
        item.CREATED_AT ||
        item.DATE ||
        item.date ||
        null;

      const typeLabel =
        item.TYPE_LABEL ||
        item.TYPE_NAME ||
        item.TRANSACTION_TYPE ||
        item.TYPE ||
        item.type ||
        null;

      const amount =
        item.AMOUNT ??
        item.amount ??
        item.VALUE ??
        null;

      const balanceAfter =
        item.BALANCE_AFTER ??
        item.BALANCE ??
        item.CURRENT_BALANCE ??
        item.balance ??
        null;

      const statusLabel =
        item.STATUS_LABEL ||
        item.STATUS_NAME ||
        item.STATUS ||
        item.status ||
        null;

      return {
        id,
        date,
        typeLabel,
        amount,
        balanceAfter,
        statusLabel,
        raw: item,
      };
    });

    // Convert Laravel 1-based pagination to 0-based indexes for the UI
    const currentPage = Number(dataBlock.current_page ?? 1);
    const lastPage = Number(dataBlock.last_page ?? 1);
    const perPageVal = Number(dataBlock.per_page ?? perPage) || perPage;
    const totalVal = Number(dataBlock.total ?? dataBlock.TOTAL ?? 0);

    const pagination = {
      page: currentPage - 1,
      perPage: perPageVal,
      total: totalVal,
      lastPage: lastPage - 1,
    };

    const result = {
      items,
      pagination,
      raw: d,
    };
    TransactionHistoryController._cache.set(requestKey, {
      timestamp: Date.now(),
      value: result,
    });
    return result;
    };

    const promise = run();
    TransactionHistoryController._inFlight.set(requestKey, promise);
    try {
      return await promise;
    } finally {
      TransactionHistoryController._inFlight.delete(requestKey);
    }
  }

  /**
   * Fetch statement history for a required date range (no pagination).
   * API: POST /v1/user/get-statement-history
   * BODY: { DATE_FROM, DATE_TO, WALLET_ID? }
   */
  static async getStatementHistory({ dateFrom, dateTo, walletId = null } = {}, options = {}) {
    const apiDateFrom = toApiDateParam(dateFrom, false);
    const apiDateTo = toApiDateParam(dateTo, true);
    if (!apiDateFrom || !apiDateTo) {
      throw new Error("Date from and date to are required.");
    }

    const body = {
      DATE_FROM: apiDateFrom,
      DATE_TO: apiDateTo,
      WALLET_ID: walletId ?? null,
    };

    const res = await postEncryptedJson(API_ROUTES.GET_STATEMENT_HISTORY, body, options);
    const d = res.decryptedData || res.data || {};
    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    const dataBlock = d.DATA || {};
    const list = Array.isArray(dataBlock.TRANSACTIONS)
      ? dataBlock.TRANSACTIONS
      : Array.isArray(dataBlock.transactions)
        ? dataBlock.transactions
        : Array.isArray(dataBlock)
          ? dataBlock
          : [];

    const items = list.map((item) => ({
      id:
        item.TRANSACTION_ID ??
        item.REF_ID ??
        item.ID ??
        item.id ??
        null,
      date:
        item.TRANSACTION_DATE ||
        item.TRX_DATE ||
        item.CREATED_AT ||
        item.DATE ||
        item.date ||
        null,
      typeLabel: item.TYPE_LABEL || item.TYPE_NAME || item.TYPE || item.type || null,
      amount: item.AMOUNT ?? item.amount ?? item.VALUE ?? null,
      balanceAfter:
        item.BALANCE_AFTER ??
        item.BALANCE ??
        item.CURRENT_BALANCE ??
        item.balance ??
        null,
      statusLabel: item.STATUS_LABEL || item.STATUS_NAME || item.STATUS || item.status || null,
      raw: item,
    }));

    return {
      items,
      totalCredit: Number(dataBlock.TOTAL_CREDIT ?? dataBlock.total_credit ?? 0),
      totalDebit: Number(dataBlock.TOTAL_DEBIT ?? dataBlock.total_debit ?? 0),
      raw: d,
    };
  }

  static async getCashinGatewayInformation(transactionId, options = {}) {
    const txId = String(transactionId || "").trim();
    if (!txId) {
      throw new Error("Transaction id is required.");
    }

    const res = await postEncryptedJson(
      API_ROUTES.GET_CASHIN_GATEWAY_INFORMATION,
      { TRANSACTION_ID: txId },
      options
    );
    const d = res.decryptedData || res.data || {};

    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    const row = d.DATA || {};
    const documents = TransactionHistoryController.extractFilePathsDeep(row);

    return {
      documents,
      raw: row,
    };
  }

  static extractFilePathsDeep(value) {
    const out = [];
    const seen = new Set();

    const pushPath = (candidate) => {
      const s = String(candidate || "").trim();
      if (!s) return;
      if (seen.has(s)) return;
      seen.add(s);
      out.push(s);
    };

    const visit = (node, depth = 0) => {
      if (node == null || depth > 12) return;
      if (Array.isArray(node)) {
        node.forEach((item) => visit(item, depth + 1));
        return;
      }
      if (typeof node !== "object") return;

      const direct =
        node.FILE_PATH ??
        node.file_path ??
        node.filePath ??
        node.PATH ??
        node.path ??
        null;
      if (direct) pushPath(direct);

      Object.values(node).forEach((child) => visit(child, depth + 1));
    };

    visit(value, 0);
    return out;
  }

  static async downloadCashinGatewayDocument(filePath, options = {}) {
    const cleanPath = String(filePath || "").trim();
    if (!cleanPath) {
      throw new Error("Document path is required.");
    }
    const response = await fetchUserFileDocument("cashinRequest", cleanPath, options);
    return response?.data;
  }
}

export default TransactionHistoryController;

